import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { getItemShop } from '../services/fortniteApi';
import ShopItem from '../components/ShopItem';
import { ShoppingBag, Clock, Calendar } from 'lucide-react';

const ItemShop = () => {
  const [shopData, setShopData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchShop = async () => {
      try {
        const data = await getItemShop();
        setShopData(data.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchShop();
  }, []);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-16 h-16 border-4 border-purple-500/20 border-t-purple-500 rounded-full animate-spin"></div>
    </div>
  );

  const allEntries = shopData?.entries || [];

  // Group entries by layout name
  const groupedEntries = allEntries.reduce((acc, entry) => {
    const category = entry.layout?.name || 'Autres Objets';
    if (!acc[category]) acc[category] = [];
    acc[category].push(entry);
    return acc;
  }, {});

  const categories = Object.keys(groupedEntries);

  return (
    <div className="pt-[calc(var(--nav-height)+2rem)] pb-20 px-4 md:px-8 max-w-[1600px] mx-auto">
      <header className="mb-12 flex flex-col md:flex-row md:items-end justify-between gap-6 px-4">
        <div>
          <div className="flex items-center gap-2 mb-2 text-purple-500">
            <ShoppingBag className="w-5 h-5" />
            <span className="text-xs font-bold uppercase tracking-widest">Boutique d'objets</span>
          </div>
          <h1 className="text-5xl font-black uppercase">Aujourd'hui</h1>
        </div>
        
        <div className="flex gap-4">
          <div className="glass-card px-6 py-3 flex items-center gap-3">
             <Calendar className="w-4 h-4 text-white/40" />
             <span className="text-sm font-medium">{new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
          </div>
          <div className="glass-card px-6 py-3 flex items-center gap-3 text-purple-400">
             <Clock className="w-4 h-4" />
             <span className="text-sm font-bold">Prochaine rotation à 01h00</span>
          </div>
        </div>
      </header>

      <div className="flex flex-col lg:flex-row gap-8">
        {/* Sidebar categories */}
        <aside className="lg:w-64 flex-shrink-0">
          <div className="sticky top-[100px] space-y-2 max-h-[calc(100vh-120px)] overflow-y-auto pr-4 scrollbar-hide">
            <p className="text-[10px] font-black uppercase text-white/30 tracking-widest mb-4 px-4">Navigation</p>
            {categories.map((category) => (
              <a 
                key={category}
                href={`#${category.replace(/\s+/g, '-').toLowerCase()}`}
                className="block px-4 py-2 rounded-xl text-sm font-medium text-white/60 hover:text-white hover:bg-white/5 transition-all truncate no-underline"
              >
                {category}
              </a>
            ))}
          </div>
        </aside>

        {/* Shop Content */}
        <div className="flex-1">
          {Object.entries(groupedEntries).map(([category, entries]) => (
            <section 
              key={category} 
              id={category.replace(/\s+/g, '-').toLowerCase()}
              className="mb-16 scroll-mt-24"
            >
              <motion.h2 
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                className="text-2xl font-black uppercase mb-8 flex items-center gap-4"
              >
                <span className="w-8 h-1 bg-purple-500 rounded-full"></span>
                {category}
                <span className="text-xs text-white/20 font-bold ml-auto">{entries.length} offres</span>
              </motion.h2>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-6">
                {entries.map((entry, idx) => {
                  const items = [
                    ...(entry.brItems || []),
                    ...(entry.tracks || []).map(t => ({ ...t, name: t.title, description: t.artist, images: { icon: t.albumArt } })),
                    ...(entry.instruments || []).map(i => ({ ...i, images: { icon: i.images.large } })),
                    ...(entry.cars || []).map(c => ({ ...c, images: { icon: c.images.large } })),
                    ...(entry.legoKits || [])
                  ];

                  return items.map((item, itemIdx) => (
                    <ShopItem 
                      key={`${idx}-${itemIdx}`} 
                      item={{
                        ...item,
                        price: entry.finalPrice
                      }} 
                    />
                  ));
                })}
              </div>
            </section>
          ))}
        </div>
      </div>
      
      {allEntries.length === 0 && (
        <div className="text-center py-20 px-8">
          <p className="text-white/40">Aucun objet n'a pu être récupéré pour le moment.</p>
        </div>
      )}
    </div>
  );
};

export default ItemShop;
